/**
 * One policy: what it is, who owns it, when it is next reviewed, who signed
 * it, and every version there has been.
 *
 * The document itself lives behind "Open document" and is a separate route —
 * this page is the record about the document, which is what a reviewer, an
 * owner and a surveyor each need before they open anything.
 */

import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { usePolicy } from './usePolicy';
import { DocumentSection } from './DocumentSection';
import { EditDetailsPanel } from './EditDetailsPanel';
import { RecordActions } from './RecordActions';
import { ApprovalHistorySection, ImportProvenanceSection, ReviewHistorySection, VersionHistorySection } from './PolicyHistory';
import { displayStatus, daysUntilReview, revisionLabel } from '../../types/policy';
import { hasAtLeast } from '../../types/roles';
import { useVisible as useOrgVisibility } from '../org/useVisibility';
import { AcknowledgeCallout } from '../acknowledgements/AcknowledgeCallout';
import { policyCapabilities } from './policyCapabilities';
import { formatDate } from './policyTable';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import styles from '../../shared/Page.module.css';

function Fact({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={styles.factTerm}>{term}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </div>
  );
}

/** An em dash, so an empty value still occupies its place in the grid. */
function Absent() {
  return <span className={styles.absent}>&mdash;</span>;
}

export function PolicyDetailPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const location = useLocation();
  const { session } = useAuth();
  const { data, isLoading, error } = usePolicy(session?.orgId ?? null, policyId);
  /**
   * ABOVE THE EARLY RETURNS, because it is a hook.
   *
   * It was below them, after the loading and not-found branches, and React
   * counted a different number of hooks on the render that found the policy
   * than on the one that was still loading: "Rendered more hooks than during
   * the previous render", and the whole page fell into the router's error
   * boundary. The unit tests could not see it, because they mock the module
   * with a plain function that is not a hook at all; the browser suite found
   * it at once.
   */
  const orgVisible = useOrgVisibility();
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (isLoading) return <Spinner label="Loading policy" />;

  if (error) {
    // The rules refuse a draft to a viewer by denying the read. Saying "you do
    // not have access" is both true and better than "not found", which would
    // send someone to ask an administrator to un-delete a policy that exists.
    const denied =
      (isCallFailure(error) && error.code === 'permission-denied') ||
      (error instanceof Error && error.message.includes('insufficient permissions'));

    return denied ? (
      <PageState title="You do not have access to this policy" tone="alert" action={{ to: '/policies', label: 'Back to policies' }}>
        It may be a draft that has not been published yet. If you believe you
        should be able to see it, ask an administrator to check your role.
      </PageState>
    ) : (
      <PageState title="Could not load this policy" tone="alert" action={{ to: '/policies', label: 'Back to policies' }}>
        {error instanceof Error ? error.message : 'Something went wrong.'}
      </PageState>
    );
  }

  if (!data) {
    return (
      <PageState title="Policy not found" action={{ to: '/policies', label: 'Back to policies' }}>
        No policy with that address exists in your organization. It may have
        been removed.
      </PageState>
    );
  }

  const roles = session?.roles ?? [];
  const author = hasAtLeast(roles, 'author');
  const status = displayStatus(data);
  const revision = revisionLabel(data);
  const days = daysUntilReview(data);
  /**
   * From the shared rule, so "Edit details" appears exactly when
   * `updatePolicyMetadata` would take it. The page used to test ownership with
   * `ownerId` alone, with no role floor and no sight of read-only content.
   */
  const can = policyCapabilities(data, { uid: session?.uid ?? null, roles });
  const canEdit = can.editDetails;
  const admin = hasAtLeast(roles, 'admin');
  const showDecision = author && data.lastDecision && (data.status === 'draft' || data.revisionStatus === 'draft');

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          {/* A grid, so the arrow lands on the TITLE's line rather than the
              line above it, while the number, name and summary keep one left
              edge. The arrow comes first in the DOM because it should come
              first in the tab order. */}
          <div className={styles.titled}>
            <p className={styles.eyebrow}>
              {data.policyNumber || 'No number'}
              {data.versionNumber != null && ` · v${data.versionNumber}`}
            </p>
            <div className={styles.titleLine}>
              <BackLink
                to={`/policies${location.search}`}
                label="Back to policies"
              />
              <h1 className={styles.title}>{data.title}</h1>
            </div>
            {data.summary && <p className={styles.subtitle}>{data.summary}</p>}
          </div>
          <div className={styles.actions}>
            <span className={`${styles.tag} ${styles[status.tone]}`}>{status.label}</span>
            {revision && <span className={`${styles.tag} ${styles.info}`}>{revision}</span>}
          </div>
        </div>
      </header>

      <div className={styles.body}>
        {notice && (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{notice}</div>
        )}

        {days != null && days < 0 && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="status">
            <strong>This policy is {Math.abs(days)} days past its review date.</strong>{' '}
            A surveyor can record that as a finding. The owner should review it: record it as reviewed with no changes below, or start a new version.
          </div>
        )}

        {(data.status === 'approved' || data.revisionStatus === 'approved') && (
          <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
            <strong>Approved, not yet effective.</strong>{' '}
            {data.heldUntil ? `It will be published on ${formatDate(data.heldUntil)}, its effective date.` : 'It is waiting to be published.'}{' '}
            {data.revisionStatus === 'approved' ? 'Staff keep reading the current version until then.' : 'Staff cannot see it until then.'}
          </div>
        )}

        {showDecision && data.lastDecision && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            <strong>
              {data.lastDecision.type === 'rejected' ? 'Rejected' : data.lastDecision.type === 'withdrawn' ? 'Withdrawn from approval' : 'Changes requested'}
              {data.lastDecision.byName && ` by ${data.lastDecision.byName}`}
              {data.lastDecision.stepName && (data.lastDecision.type === 'withdrawn' ? ` while waiting on ${data.lastDecision.stepName}` : ` at ${data.lastDecision.stepName}`)}
              {data.lastDecision.at && ` on ${formatDate(data.lastDecision.at)}`}.
            </strong>{' '}
            {data.lastDecision.comments ?? 'No reason was given.'}
          </div>
        )}

        {/*
          * RETIREMENT EVIDENCE, AS THE SERVER RECORDED IT. Finding C.
          *
          * The record has always kept who retired it and why; the page showed
          * the date and dropped the rest. Everything here is read from the
          * record. A legacy retirement that stored no name says so rather than
          * having one invented for it.
          */}
        {data.status === 'retired' && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            <p>
              <strong>Retired{data.retiredAt && ` on ${formatDate(data.retiredAt)}`}.</strong>{' '}
              {data.retiredByName
                ? `By ${data.retiredByName}.`
                : data.retiredByUid
                  ? 'By a person the member list no longer names.'
                  : 'Who retired it was not recorded.'}
            </p>
            <p>{data.retiredReason ?? 'No reason was recorded. Staff no longer see it; the record and its versions are kept.'}</p>
            {data.supersededById && (
              <p>
                Superseded by{' '}
                <Link to={`/policy/${data.supersededById}`}>the policy that replaced it</Link>.
              </p>
            )}
          </div>
        )}

        {/*
          * WORK THE SYSTEM OWES ON THIS DOCUMENT. Findings L and M.
          *
          * A publication that failed used to be retried silently every hour
          * with nothing on screen, and an audience the publication could not
          * finish asking was recorded as zero assigned. An administrator can
          * see both now, in the place they would look.
          */}
        {admin && data.publicationFailure && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            <p>
              <strong>This document has not finished publishing.</strong>{' '}
              StandingDocs has tried {data.publicationFailure.attempts === 1 ? 'once' : `${data.publicationFailure.attempts} times`}
              {data.publicationFailure.lastAttemptAt && `, most recently on ${formatDate(data.publicationFailure.lastAttemptAt)}`}
              , and will keep trying every hour.
            </p>
            {data.publicationFailure.lastError && <p>The last attempt said: {data.publicationFailure.lastError}</p>}
          </div>
        )}

        {admin && data.acknowledgementAssignment && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="alert">
            <p>
              <strong>Not everyone has been asked to read this yet.</strong>{' '}
              Assigning it to its audience did not finish
              {data.acknowledgementAssignment.expected != null
                && data.acknowledgementAssignment.assigned != null
                && ` (${data.acknowledgementAssignment.assigned} of ${data.acknowledgementAssignment.expected})`}
              . StandingDocs will finish it automatically; the acknowledgement report is incomplete until it does.
            </p>
          </div>
        )}

        {/*
          SEARCH'S COPY IS BEHIND, AND SOMEBODY CAN SEE IT. Finding R6.

          Firestore is the policy; Algolia is a projection of it. A failure to
          update the projection never blocks publishing and never changes what
          the policy says, and it used to be completely invisible: the document
          was live, correct, and unfindable by search. An hourly job retries;
          this is what says so meanwhile, to the people who would be asked why
          a policy cannot be found.
        */}
        {admin && data.searchIndex && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            <p>
              <strong>Search has not been updated for this policy yet.</strong>{' '}
              The policy itself is correct and staff can open it from the library; only
              the search index is behind. StandingDocs is retrying every hour
              {data.searchIndex.attempts > 1 && ` (${data.searchIndex.attempts} attempts so far)`}.
            </p>
            {data.searchIndex.lastError && <p>The last attempt said: {data.searchIndex.lastError}</p>}
          </div>
        )}

        {/*
          WHAT THIS PAGE OWES A READER, FIRST. Every acknowledgement email
          links here, and until 2026-09-23 the page stated the requirement as
          a fact and gave nobody a way to meet it (finding R8). It is offered
          only to somebody who actually holds an actionable requirement for
          the version in force.
        */}
        <AcknowledgeCallout
          policyId={data.id}
          currentVersionId={data.currentVersionId}
          policyStatus={data.status}
        />

        <DocumentSection policy={data} />

        <section className={styles.section}>
          <div className={styles.headerRow} style={{ alignItems: 'baseline' }}>
            <h2 className={styles.sectionTitle}>Details</h2>
            {canEdit && !editing && (
              <button type="button" className={styles.btn} onClick={() => { setEditing(true); setNotice(null); }}>
                Edit details…
              </button>
            )}
          </div>
          {editing ? (
            <EditDetailsPanel policy={data} onClose={() => setEditing(false)} onSaved={setNotice} />
          ) : (
            <dl className={styles.facts}>
              <Fact term="Owner">{data.ownerName ?? <Absent />}</Fact>
              <Fact term="Department">
                {data.departmentNames.length ? data.departmentNames.join(', ') : <Absent />}
              </Fact>
              <Fact term="Category">{data.categoryName ?? <Absent />}</Fact>
              <Fact term="Document type">{data.documentTypeName ?? <Absent />}</Fact>
              <Fact term="Effective">
                {data.effectiveDate ? (
                  <span className="tnum">{formatDate(data.effectiveDate)}</span>
                ) : (
                  <Absent />
                )}
              </Fact>
              <Fact term="Next review">
                {data.reviewDate ? (
                  <span className="tnum">{formatDate(data.reviewDate)}</span>
                ) : (
                  <Absent />
                )}
              </Fact>
              <Fact term="Review cycle">
                {data.reviewCycleMonths != null ? `${data.reviewCycleMonths} months` : <Absent />}
              </Fact>
              <Fact term="Acknowledgement">
                {data.requiresAcknowledgement
                  ? data.acknowledgementDueDays != null
                    ? `Required within ${data.acknowledgementDueDays} days`
                    : 'Required'
                  : 'Not required'}
              </Fact>
              {data.requiresAcknowledgement && data.targetAudience !== 'all' && (
                <Fact term="Asked of">
                  {data.targetAudience === 'departments' ? 'Certain departments' : 'Certain roles'}
                </Fact>
              )}
            </dl>
          )}
        </section>

        {(data.regulatoryStandardLabels.length > 0 || data.tags.length > 0) && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Standards and tags</h2>
            <div className={styles.chips}>
              {data.regulatoryStandardLabels.map((s) => (
                <span key={s} className={styles.chip}>{s}</span>
              ))}
              {data.tags.map((t) => (
                <span key={t} className={styles.chip}>{t}</span>
              ))}
            </div>
          </section>
        )}

        {data.notes && author && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Notes</h2>
            <p style={{ margin: 0, maxWidth: '70ch', lineHeight: 1.6 }}>{data.notes}</p>
          </section>
        )}

        <RecordActions policy={data} onDone={setNotice} />

        {/*
        * EVIDENCE IS NOT A DECISION. The callable serves a published policy's
        * approval record to any member, because what was approved and by whom
        * is not secret from the people it applies to, and an unpublished one
        * only to the roles that write and approve documents. The page used to
        * gate the whole section at `author`, so a reader the server would have
        * served had no route to it at all. Nothing here grants a decision:
        * approving and returning live on My approvals, behind their own gate.
        */}
      {(data.status === 'published' || author) && orgVisible.approvalRecord
        && <ApprovalHistorySection policyId={data.id} />}

        <VersionHistorySection policy={data} orgId={session?.orgId ?? null} admin={hasAtLeast(roles, 'admin')} />
        {/* Every "Reviewed, no changes" the product has recorded. It wrote
            them from the first release and read them back nowhere. */}
        <ReviewHistorySection policy={data} orgId={session?.orgId ?? null} />
        {/* Only for a document that arrived by migration, and read from the
            policy itself so deleting the import job does not erase it. */}
        <ImportProvenanceSection policy={data} />
      </div>
    </>
  );
}
